import React, { useState, useEffect, useCallback } from 'react';

import PageTitle from '../PageTitle/page_title';
import i18next from "i18next";
import { Button, Modal } from 'react-bootstrap';
import { PastChecklistsService, TimeFormattingService, UserUnitService } from '../../services';
import { DatePickerInput } from '../DatePickerInput';
import SimpleLineGraph from '../Graphs/simple_line_graph';
import { connect } from 'react-redux';
import FontAwesomeIcon from "@fortawesome/react-fontawesome";
import { faPlus, faTrash } from '@fortawesome/fontawesome-free-solid/';
import { SelectInput } from '../SelectInput';
import moment from 'moment-timezone';

const AddSetPointModal = ({ me, show, onAdd, onHide }) => {
    const comparisonOptions = [ { label: '< (Under)', value: 'under' }, { label: '> (Over)', value: 'over' } ];

    const [comparison, setComparison] = useState(comparisonOptions[0]);
    const [value, setValue] = useState('');

    const doAdd = useCallback(() => {
        onAdd(comparison.value, parseFloat(value));
        setComparison(comparisonOptions[0]);
        setValue('');
    }, [onAdd, comparison, value]);

    return (
        <Modal show={show} onHide={onHide}>
            <Modal.Header closeButton>
                <Modal.Title>Add Set Point</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <table style={{padding: 30}}>
                    <tbody>
                        <tr>
                            <td style={{padding: 5}}>Comparison:</td>
                            <td style={{padding: 5}}>
                                <SelectInput
                                    name="comparison"
                                    input={{ value: comparison, onChange: (sel) => setComparison(sel) }}
                                    options={comparisonOptions}
                                    meta={{}}                                    
                                />
                            </td>
                        </tr>
                        <tr><td></td><td></td></tr>
                        <tr>
                            <td style={{padding: 5}}>Value:</td>
                            <td style={{padding: 5}}><input type='text' name='value' onChange={(e) => setValue(e.target.value)} value={value} /> {me.temperatureDisplayUnit}</td>
                        </tr>
                    </tbody>
                </table>
            </Modal.Body>
            <Modal.Footer>
                <Button className="btn btn-danger" onClick={doAdd} disabled={comparison == null || isNaN(parseFloat(value))}>{i18next.t('add')}</Button>
                <Button onClick={onHide}>{i18next.t('form-labels.cancel')}</Button>
            </Modal.Footer>
        </Modal>
    );
}

const ChecklistPassiveTempReportForm = ({ me, onSubmit }) => {
    const [startAtDate, setStartAtDate] = useState(null);
    const [endAtDate, setEndAtDate] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [setPoints, setSetPoints] = useState(localStorage.getItem("passiveChecklistReportSetPoints") ? JSON.parse(localStorage.getItem("passiveChecklistReportSetPoints")) : []);

    const doSubmit = useCallback((event) => {
        event.preventDefault(); 
        onSubmit({ startAtDate: moment(startAtDate).startOf("day").valueOf(), endAtDate: moment(endAtDate).endOf("day").valueOf(), setPoints: setPoints });
    }, [onSubmit, startAtDate, endAtDate, setPoints]);

    const addSetPoint = useCallback((comparison, value) => {
        const result = [...setPoints].concat([{comparison: comparison, value: value}]);
        setSetPoints(result);
        if (result.length == 0) {
            localStorage.removeItem("passiveChecklistReportSetPoints");
        } else {
            localStorage.setItem("passiveChecklistReportSetPoints", JSON.stringify(result));
        }
        setShowModal(false);
    }, [setPoints]);

    const removeSetPoint = useCallback((event, index) => {
        event.preventDefault();
        let result = [...setPoints];
        result.splice(index, 1);
        if (result.length == 0) {
            localStorage.removeItem("passiveChecklistReportSetPoints");
        } else {
            localStorage.setItem("passiveChecklistReportSetPoints", JSON.stringify(result));
        }
        setSetPoints(result);
    }, [setPoints]);

    return (
        <div className="op-content-box filter-box" style={{marginBottom: 20}}>
            <form onSubmit={doSubmit}>
                <ul className="list-inline clearfix">
                    <li className="pull-left box-label"><h4 className="remove-margin-bottom">{ i18next.t('filters') }</h4></li>
                </ul>

                <div className = "row">
                    <div className="col-sm-2">
                        <div className="align-date-picker pull-left">
                            <label>{ i18next.t('form-labels.start-date') }</label>
                            <DatePickerInput input={{value: startAtDate}} meta={{}} onDayChange={setStartAtDate} />
                        </div>
                    </div>
                    <div className="col-sm-2">
                        <div className="align-date-picker pull-left">
                            <label>End Date</label>
                            <DatePickerInput input={{value: endAtDate}} meta={{}} onDayChange={setEndAtDate} />
                        </div>
                    </div>
                    <div className="col-sm-6">
                        <div className="pull-left">
                            <label>Set Points</label>
                            {
                                setPoints.map((setPoint, key) => 
                                    <span key={key} style={{paddingRight: 10}}>
                                        {setPoint.comparison == 'under' ? '<' : '>'} {setPoint.value} {me.temperatureDisplayUnit}
                                        &nbsp;
                                        <button className="btn btn-sm btn-danger" style={{ color: "#fff" }} onClick={(e) => removeSetPoint(e, key)}><FontAwesomeIcon icon={faTrash} /></button>
                                    </span>
                                )
                            }
                            &nbsp;
                            <button className="btn btn-sm btn-default" onClick={(e) => {e.preventDefault(); setShowModal(true)}}><FontAwesomeIcon icon={faPlus} /></button>
                        </div>
                    </div>
                    <div className="pull-right" style={{paddingRight: 20}}>
                        <label style={{visibility: 'hidden'}}>{ i18next.t('form-labels.run-report') }</label>
                        <ul className="list-inline clearfix">
                            <li className="pull-left" style={{marginLeft: '15px'}}>
                                <button type="submit" className="btn btn-sm btn-default pull-right" disabled={!(startAtDate && endAtDate)} >{ i18next.t('form-labels.run-report') }</button>
                            </li>
                        </ul>
                    </div>

                    <AddSetPointModal me={me} show={showModal} onHide={() => setShowModal(false)} onAdd={addSetPoint} />
                </div>
            </form>
        </div>
    );
}

const SetPointArea = ({ me, setPoint, timezone, readings }) => {    
    const calculateComparison = useCallback((comparison, value, originalReadings) => {
        const readings = originalReadings.sort((a, b) => a.timestamp - b.timestamp);
        const comparisonValue = UserUnitService.convertFromUserUnitToSystemUnit(me.temperatureDisplayUnit, value);
        if (comparison == 'under') {
            for (let index = 0; index < readings.length; index++) {
                if (readings[index].reading < comparisonValue) {
                    return ` at ${TimeFormattingService.renderNormalTime(readings[index].timestamp, timezone)}`;
                }
            }
        } else {
            for (let index = 0; index < readings.length; index++) {
                if (readings[index].reading > comparisonValue) {
                    return ` at ${TimeFormattingService.renderNormalTime(readings[index].timestamp, timezone)}`;
                }
            }
        }
        return "Never";
    }, [me]);

    return (
        <div style={{paddingTop: 10}}>
            <strong>{setPoint.comparison == 'under' ? "<" : ">"} {setPoint.value}</strong> { calculateComparison(setPoint.comparison, setPoint.value, readings) }
        </div>
    );
};


const ChecklistPassiveTempReportResults = ({ me, query, onError }) => {
    const [reportData, setReportData] = useState(null);

    useEffect(() => {
        setReportData(null);

        PastChecklistsService.getPastChecklistPassiveReportData(query.startAtDate, query.endAtDate).then(({ report }) => {
            setReportData(report);
        }).catch((error) => {
            console.error(error);
            onError("Error fetching report.");
        })
    }, [query]);

    if (reportData) {
        return (
            <div>
                <div className="op-content-box">
                    <ul className="list-inline clearfix">
                        <li className="pull-left box-label"><h4 className="remove-margin-bottom">{ i18next.t('results') }</h4></li>
                    </ul>

                    {
                        reportData.entries.map((entry, ek) => (
                            <div key={ek}>
                                <h2>{entry.checklistName} {entry.description ? `: ${entry.description}` : ''}</h2>

                                {
                                    entry.details.map((detail, dk) => (
                                        <div key={`${ek}-${dk}`}>
                                            <strong>{detail.name}</strong>

                                            <div style={{paddingTop: 10}}>
                                                <strong>Started at</strong>: 
                                                {TimeFormattingService.renderNormalTime(detail.startedAt, entry.timezone)} {detail.startedBy ? `by ${detail.startedBy}` : ''}
                                            </div>

                                            { query.setPoints.map((setPoint, spk) => <SetPointArea me={me} setPoint={setPoint} timezone={entry.timezone} key={`${dk}-${spk}`} readings={detail.readings} />) }

                                            <div style={{paddingTop: 10, paddingBottom: 10}}>
                                                <strong>Completed at</strong>: 
                                                {TimeFormattingService.renderNormalTime(detail.completedAt, entry.timezone)} {detail.completedBy ? `by ${detail.completedBy}` : ''}
                                            </div>

                                            { detail.readings.length > 0 && <SimpleLineGraph 
                                                rawData={detail.readings} 
                                                dataTypeLabel='Temperature' 
                                                timezone={entry.timezone} 
                                                displayName={detail.deviceName}
                                                setPoints={query.setPoints.map((setPoint) => setPoint.value)}
                                                hideBrush={true}
                                            /> }
                                            { detail.readings.length == 0 && <div>No data</div> }
                                        </div>
                                    ))
                                }
                            </div>
                        ))
                    }
                </div>
            </div>
        );
    } else {
        return <div className="loader">{i18next.t("loading")}</div>;
    }
};

const ChecklistPassiveTempReport = connect(({ user }) => ({ me: user.me }))(({ me }) => {
    const [errorMessage, setErrorMessage] = useState(null);
    const [query, setQuery] = useState(null);

    return (
        <div>
            <div className="row">
                <PageTitle title='Passive Temperature Checklist' />
            </div>
            { errorMessage && <div>{errorMessage}</div> }
            <div className="device-detail-report col-sm-12">
                <ChecklistPassiveTempReportForm onSubmit={setQuery} me={me} />
            </div>
            <div className = "device-detail-report col-sm-12">
                {!errorMessage && query && <ChecklistPassiveTempReportResults query={query} onError={setErrorMessage} me={me} /> }
            </div>
        </div>
    );
});

export default ChecklistPassiveTempReport;